import {Box, Typography} from '@mui/material';
import React, {LegacyRef, useCallback, useRef} from 'react';
import {
  DragHandleProps,
  RuleProps,
  useRule,
  useStopEventPropagation,
} from 'react-querybuilder';

import {useDispatch} from 'react-redux';
import {setEditField} from '../../../redux/slices/querySlice';
import {QBOperators} from '../../../utils/queryBuilder';
import AurButton from '../../common/aur-button/AurButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {
  deleteCustomRuleById,
  getCustomRuleById,
  setCustomRule,
} from './useCustomRule';
import {t} from 'i18next';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';

function getGlobalRuleIndex(builder: any, ruleId: string): number | null {
  const ruleIds: string[] = [];

  const traverse = (node: any) => {
    if (node?.rules && Array.isArray(node?.rules)) {
      for (const item of node.rules) {
        // Items can be strings (combinators) or objects (rule or group)
        if (typeof item === 'object') {
          // If the item has a "field", assume it’s a rule.
          if ('field' in item) {
            ruleIds.push(item.id);
          } else if ('rules' in item) {
            // If it's a group, traverse its rules.
            traverse(item);
          }
        }
      }
    }
  };

  traverse(builder);
  const idx = ruleIds.findIndex((id) => id === ruleId);
  return idx >= 0 ? idx + 1 : null;
}

export const CustomDragHandle = React.forwardRef<HTMLElement, DragHandleProps>(
  (props, ref) => {
    // You can use the props if needed for styling or behavior.
    return (
      <span ref={ref} style={{cursor: 'grab'}}>
        <AurIcon
          size={'medium'}
          icon={IconNames.DragIndicator}
          color={IconColors.Custom}
          customClassName={`query-draggable-icon`}
        />
      </span>
    );
  }
);

CustomDragHandle.displayName = 'CustomDragHandle';

// Alternatively, you can provide it as a function returning the component:
// dragHandle: (props: DragHandleProps) => <CustomDragHandle {...props} />,

export const CustomRule: React.FC<RuleProps> = (props) => {
  const globalIndex = getGlobalRuleIndex(
    props.schema.getQuery(),
    props.id ?? ''
  );
  const r = useRule(props);
  const removeRule = useStopEventPropagation(r.removeRule);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const fieldData = getCustomRuleById(r.id);
  const rule = r.rule as typeof r.rule & {fieldType?: string};
  const fieldTitle =
    rule?.fieldType === "tag"
      ? rule.field.replace(/[<>]/g, '')
      : r.fieldData.label;
  const startIndexRef = useRef<any>();

  const onChangeOperator = useCallback(
    (value: any) => {
      if (
        value === QBOperators.isSet ||
        value === QBOperators.isNotSet ||
        value === QBOperators.isNotSetText ||
        value === QBOperators.isSetText
      ) {
        r.onChangeValue('*');
        r.id && setCustomRule(r.id, {valueLabel: '*'});
      }
      r.onChangeOperator(value);
    },
    [r]
  );

  return (
    <div
      ref={r.dndRef}
      data-dragmonitorid={r.dragMonitorId}
      data-dropmonitorid={r.dropMonitorId}
      className={r.outerClassName}
    >
      <div
        ref={r.dragRef as LegacyRef<HTMLDivElement> | undefined}
        className="draggable-icon-container"
        draggable
        onDragStart={() => {
          startIndexRef.current = getGlobalRuleIndex(
            props.schema.getQuery(),
            r.id ?? ''
          );
        }}
        onDragEnd={() => {
          const endIndex = getGlobalRuleIndex(
            props.schema.getQuery(),
            r.id ?? ''
          );
          if (
            startIndexRef.current !== null &&
            endIndex !== null &&
            startIndexRef.current !== endIndex
          ) {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.queryBuilder.move'),
                type: GA_CLICK_TYPES.ICON,
                region: GA_REGIONS.BODY,
                action: t('gaTracking.queryBuilder.moveAction'),
              },
            });
          }
          startIndexRef.current = null;
        }}
      >
        <r.schema.controls.dragHandle />
      </div>
      <Box className="rule-content">
        <Box className="first-row">
          <Typography className="typo-text subhead4_size">
            {globalIndex}. {fieldTitle}
          </Typography>
          {/* Row for Operator */}
          <Typography className="operator-selector">
            {r.field === '<DefaultFields>' ? (
              '(Contains)'
            ) : (
              <r.schema.controls.operatorSelector
                value={r.rule.operator}
                options={r.operators}
                handleOnChange={onChangeOperator}
                disabled={r.disabled}
              />
            )}
          </Typography>
        </Box>

        {/* Row for Value */}
        <Box className="second-row">{fieldData?.valueLabel ?? ''}</Box>
      </Box>
      <Box className="rule-action-btn-container">
        <AurButton
          type="secondary"
          classNames="buttonSmall_Size rule-action-icon-button"
          dataTestId="edit-rule"
          ariaLabel={t('queryBuilder.editRule')}
          onClick={() => {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.global.edit'),
                type: GA_CLICK_TYPES.ICON,
                region: GA_REGIONS.BODY,
                action: t('gaTracking.queryBuilder.editAction'),
              },
            });
            dispatch(
              setEditField({
                id: r.id || '',
                operator: r.operator || '',
                value: r.value || '',
                path: r.path,
              })
            );
          }}
        >
          <AurIcon icon={IconNames.Edit} />
        </AurButton>
        <AurButton
          type="secondary"
          onClick={(event) => {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.DELETE,
              click: {
                name: t('gaTracking.global.delete'),
                type: GA_CLICK_TYPES.ICON,
                region: GA_REGIONS.BODY,
                action: t('gaTracking.queryBuilder.deleteAction'),
              },
            });
            removeRule(event);
            deleteCustomRuleById(r.id);
          }}
          ariaLabel={t('queryBuilder.deleteRule')}
          classNames="buttonSmall_Size rule-action-icon-button"
        >
          <AurIcon icon={IconNames.Remove} />
        </AurButton>
      </Box>
      {/* <RuleComponents {...r} /> */}
    </div>
  );
};
